
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';

export default function CommercialAgendaPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [theme, setTheme] = useState(localStorage.getItem('commercial_theme') || 'light');

    useEffect(() => {
        localStorage.setItem('commercial_theme', theme);
    }, [theme]);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [creatingSlot, setCreatingSlot] = useState(null); // { date, hour }
    const [selectedMeeting, setSelectedMeeting] = useState(null); // For details
    const [hoveredSlot, setHoveredSlot] = useState(null); // { dayIndex, hour }
    const [newTitle, setNewTitle] = useState('');

    // Calculate week start (Monday)
    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    };

    const weekStart = getWeekStart(currentDate);
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const hours = Array.from({ length: 10 }, (_, i) => i + 9); // 9h to 18h

    useEffect(() => {
        if (user?.id) {
            loadMeetings();
        }
    }, [weekStart, user?.id]);

    const loadMeetings = async () => {
        setLoading(true);
        try {
            // Fetch meetings for this commercial only
            const res = await pb.collection('meetings').getFullList({
                sort: '-start',
                filter: `commercial_id = "${user.id}"`
            });
            setMeetings(res);
        } catch (e) {
            console.error("Error loading meetings", e);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSlot = (dayIndex, hour) => {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + dayIndex);
        date.setHours(hour, 0, 0, 0);
        setCreatingSlot({ date, hour });
        setNewTitle(''); // Reset
    };

    const confirmCreateSlot = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!newTitle) return;
        if (!user?.id) return window.alert("Erreur: Utilisateur non identifié.");

        setSubmitting(true);
        try {
            const startStr = creatingSlot.date.toISOString();
            const endStr = new Date(creatingSlot.date.getTime() + 60 * 60 * 1000).toISOString();

            await pb.collection('meetings').create({
                title: newTitle,
                start: startStr,
                end: endStr,
                commercial_id: user.id,
                created_by: user.id
            });

            setCreatingSlot(null); // Close modal IMMEDIATELY
            loadMeetings(); // Background reload
        } catch (err) {
            console.error(err);
            window.alert("Erreur lors de la création : " + (err.message || err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm("Supprimer ce RDV ?")) return;
        try {
            await pb.collection('meetings').delete(id);
            loadMeetings();
        } catch (err) { alert(err.message); }
    };

    const changeWeek = (delta) => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + (delta * 7));
        setCurrentDate(d);
    };

    const isLight = theme === 'light';
    const styles = {
        bg: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.9)',
        text: isLight ? '#1e293b' : 'white',
        border: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        cardBg: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
        cardHeader: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
        today: isLight ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
        btnClose: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
        btnHover: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
        input: isLight ? { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#0f172a' } : { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }
    };

    // Close Handler -> Navigate back to launchpad
    const handleClose = () => {
        navigate('/commercial/launchpad');
    };

    return (
        <div style={{ height: '100vh', width: '100vw', background: styles.bg, display: 'flex', flexDirection: 'column', padding: 40, color: styles.text }}>

            {/* NEW MEETING MODAL */}
            {creatingSlot && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 310, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={() => setCreatingSlot(null)}>
                    <div onClick={e => e.stopPropagation()} className="animate-scale-in" style={{ width: 400, background: isLight ? 'white' : '#1e293b', padding: 30, borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : 'white' }}>
                        <h3 style={{ marginTop: 0, marginBottom: 20 }}>Nouveau Rendez-vous</h3>
                        <div style={{ marginBottom: 20, opacity: 0.7 }}>
                            {creatingSlot.date.toLocaleDateString()} à {creatingSlot.hour}h00
                        </div>
                        <form onSubmit={confirmCreateSlot}>
                            <input
                                autoFocus
                                placeholder="Titre (ex: Point Projet, Démo...)"
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                                style={{ width: '100%', padding: 15, borderRadius: 10, outline: 'none', marginBottom: 20, ...styles.input }}
                            />
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setCreatingSlot(null)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer' }}>Annuler</button>
                                <button type="submit" disabled={!newTitle || submitting} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 600, cursor: 'pointer' }}>{submitting ? 'Enregistrement...' : 'Valider'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MEETING DETAILS MODAL */}
            {selectedMeeting && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 320, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedMeeting(null)}>
                    <div onClick={e => e.stopPropagation()} className="animate-scale-in" style={{ width: 400, background: isLight ? 'white' : '#1e293b', padding: 30, borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', color: isLight ? '#0f172a' : 'white', position: 'relative' }}>
                        <button onClick={() => setSelectedMeeting(null)} style={{ position: 'absolute', top: 15, right: 15, background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'inherit', opacity: 0.5 }}>&times;</button>

                        <h3 style={{ marginTop: 0, marginBottom: 5, fontSize: '1.5rem' }}>{selectedMeeting.title}</h3>
                        <div style={{ opacity: 0.7, marginBottom: 20, fontSize: '0.9rem' }}>
                            📅 {new Date(selectedMeeting.start).toLocaleDateString()} <br />
                            ⏰ {new Date(selectedMeeting.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(selectedMeeting.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>

                        <div style={{ padding: 15, background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.05)', borderRadius: 10, marginBottom: 20 }}>
                            <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Invité par : Vous</div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={(e) => { setSelectedMeeting(null); handleDelete(selectedMeeting.id, e); }} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 600, cursor: 'pointer' }}>Supprimer</button>
                            <button onClick={() => setSelectedMeeting(null)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', color: 'inherit', fontWeight: 600, cursor: 'pointer' }}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <button onClick={handleClose} style={{ width: 40, height: 40, borderRadius: '50%', background: styles.btnClose, border: 'none', color: styles.text, fontSize: '1.2rem', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>←</button>
                    <div>
                        <h2 style={{ fontSize: '2rem', margin: 0, fontWeight: 700 }}>📅 Agenda Commercial</h2>
                        <p style={{ opacity: 0.6, margin: 0 }}>Gérez vos disponibilités en temps réel</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    {/* Theme Toggle */}
                    <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} style={{ padding: '8px 15px', borderRadius: 20, background: styles.cardBg, border: 'none', color: styles.text, cursor: 'pointer', fontWeight: 600 }}>
                        {theme === 'light' ? '🌙 Mode Sombre' : '☀️ Mode Clair'}
                    </button>

                    <div style={{ display: 'flex', background: styles.cardBg, borderRadius: 10, padding: 5 }}>
                        <button onClick={() => changeWeek(-1)} style={{ padding: '8px 15px', background: 'none', border: 'none', color: styles.text, cursor: 'pointer' }}>←</button>
                        <span style={{ padding: '8px 15px', fontWeight: 600 }}>Semaine du {weekStart.toLocaleDateString()}</span>
                        <button onClick={() => changeWeek(1)} style={{ padding: '8px 15px', background: 'none', border: 'none', color: styles.text, cursor: 'pointer' }}>→</button>
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', gap: 1, background: styles.border, border: `1px solid ${styles.border}`, borderRadius: 16, overflow: 'hidden' }}>

                {/* Header Row (Days) */}
                <div style={{ background: styles.cardHeader }}></div> {/* Time Corner */}
                {days.map((day, i) => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() + i);
                    const isToday = d.toDateString() === new Date().toDateString();
                    return (
                        <div key={day} style={{ padding: 15, textAlign: 'center', background: isToday ? styles.today : styles.cardHeader, fontWeight: 600, borderBottom: `1px solid ${styles.border}`, color: styles.text }}>
                            {day} <span style={{ opacity: 0.6, fontSize: '0.8em' }}>{d.getDate()}</span>
                        </div>
                    )
                })}

                {/* Time Rows */}
                {hours.map(hour => (
                    <React.Fragment key={hour}>
                        {/* Time Label */}
                        <div style={{ padding: 10, textAlign: 'right', fontSize: '0.8rem', opacity: 0.5, borderRight: `1px solid ${styles.border}`, borderBottom: `1px solid ${styles.border}`, background: styles.bg }}>
                            {hour}:00
                        </div>

                        {/* Slots */}
                        {days.map((_, dayIndex) => {
                            // Find meeting
                            const slotDate = new Date(weekStart);
                            slotDate.setDate(slotDate.getDate() + dayIndex);

                            // Check matches
                            const meeting = meetings.find(m => {
                                const mDate = new Date(m.start);
                                return mDate.getDate() === slotDate.getDate() &&
                                    mDate.getMonth() === slotDate.getMonth() &&
                                    mDate.getHours() === hour;
                            });

                            return (
                                <div key={dayIndex}
                                    onClick={() => !meeting && handleCreateSlot(dayIndex, hour)}
                                    style={{
                                        borderRight: `1px solid ${styles.border}`,
                                        borderBottom: `1px solid ${styles.border}`,
                                        position: 'relative', cursor: meeting ? 'default' : 'pointer',
                                        transition: 'background 0.2s',
                                        background: (!meeting && hoveredSlot?.dayIndex === dayIndex && hoveredSlot?.hour === hour) ? styles.cardBg : styles.bg
                                    }}
                                    onMouseEnter={() => !meeting && setHoveredSlot({ dayIndex, hour })}
                                    onMouseLeave={() => setHoveredSlot(null)}
                                >
                                    {!meeting && hoveredSlot?.dayIndex === dayIndex && hoveredSlot?.hour === hour && (
                                        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: styles.text, opacity: 0.5, fontSize: '1.5rem', userSelect: 'none' }}>+</div>
                                    )}

                                    {meeting && (
                                        <div
                                            onClick={() => setSelectedMeeting(meeting)}
                                            className="animate-scale-in" style={{
                                                position: 'absolute', inset: 2, borderRadius: 8,
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                padding: 8, fontSize: '0.8rem', overflow: 'hidden',
                                                boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                                                color: 'white' // Always white text on blue card
                                            }}>
                                            <div style={{ fontWeight: 600 }}>{meeting.title}</div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </React.Fragment>
                ))}
            </div>
            <style>{`
                .animate-scale-in { animation: scaleIn 0.2s ease-out; }
                @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
            `}</style>
        </div>
    );
}
